import React, { useCallback, useEffect, useState } from 'react';
import '../Temporadas/Temporadas.css';
import './AltaUsuario.css';
import { useAuth } from '../../context/AuthContext';
import {
  actualizarUsuario, crearUsuario, enviarAcceso, getJugadoresSinUsuario, getUsuarios, getVistaPreviaAcceso,
} from '../../services/usuariosService';
import { proponerUsuario } from '../../utils/proponerUsuario';

export const ROLES = ['ADMIN', 'COMITE', 'JUGADOR'];
const FORM_VACIO = { usuario: '', email: '', rol: 'JUGADOR', jugadorId: '', enviarAcceso: true };

function SelectJugador({ jugadores, value, onChange, obligatorio, disabled, etiqueta }) {
  return (
    <select aria-label={etiqueta} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      <option value="">{obligatorio ? '— Elige jugador —' : '— Sin jugador —'}</option>
      {jugadores.map((j) => <option key={j.id} value={j.id}>{j.nombre}</option>)}
    </select>
  );
}

// Una fila de la tabla: rol y jugador se editan en borrador y se guardan con un botón.
// La `key` incluye rol y jugador guardados: al guardar, la fila se remonta con el borrador limpio.
function FilaUsuario({ u, jugadores: libres, esYo, ocupado, onGuardar, onEnviar, onActivo }) {
  const [rol, setRol] = useState(u.rol);
  const [jugadorId, setJugadorId] = useState(u.jugadorId ?? '');

  // Libres + el que ya tiene vinculado (no está en la lista de libres).
  const jugadores = u.jugadorId ? [{ id: u.jugadorId, nombre: u.jugadorNombre }, ...libres] : libres;
  const cambiado = rol !== u.rol || String(jugadorId) !== String(u.jugadorId ?? '');
  const falta = rol === 'JUGADOR' && jugadorId === '';

  return (
    <tr className={u.activo ? '' : 'usuario-inactivo'}>
      <td><strong>{u.usuario}</strong>{esYo && <span className="usuario-yo"> (tú)</span>}</td>
      <td>{u.email}</td>
      <td>
        <select aria-label={`Rol de ${u.usuario}`} value={rol} disabled={ocupado || (esYo && u.rol === 'ADMIN')}
          onChange={(e) => setRol(e.target.value)}>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </td>
      <td>
        <SelectJugador jugadores={jugadores} value={jugadorId} obligatorio={rol === 'JUGADOR'} disabled={ocupado}
          etiqueta={`Jugador de ${u.usuario}`} onChange={setJugadorId} />
      </td>
      <td>
        {!u.activo ? 'Desactivado' : u.debeCambiarPassword ? 'Pendiente de cambiar contraseña' : 'Activo'}
      </td>
      <td className="usuario-acciones">
        <button type="button" className="btn btn-primary" disabled={ocupado || !cambiado || falta}
          onClick={() => onGuardar(u, { rol, jugadorId: jugadorId === '' ? null : Number(jugadorId) })}>
          Guardar
        </button>
        <button type="button" className="btn btn-secondary" disabled={ocupado || !u.activo} onClick={() => onEnviar(u)}>
          Enviar acceso
        </button>
        {!esYo && (
          <button type="button" className="btn btn-secondary" disabled={ocupado} onClick={() => onActivo(u, !u.activo)}>
            {u.activo ? 'Desactivar' : 'Activar'}
          </button>
        )}
      </td>
    </tr>
  );
}

// /dashboard/alta-usuario (solo ADMIN): alta de cuentas, cambio de rol, vínculo
// con un jugador y envío de las credenciales por correo. Un COMITE/ADMIN puede
// ser también jugador: el rol y el jugador vinculado son independientes.
export default function AltaUsuario() {
  const { usuario: yo } = useAuth();
  const esAdmin = yo?.rol === 'ADMIN';
  const [usuarios, setUsuarios] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [alta, setAlta] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [confirmar, setConfirmar] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [us, js] = await Promise.all([getUsuarios(), getJugadoresSinUsuario()]);
      setUsuarios(us);
      setJugadores(js);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la lista.');
    }
  }, []);

  useEffect(() => { if (esAdmin) cargar(); }, [esAdmin, cargar]);

  // Correo que recibirá la persona, recalculado al cambiar usuario o jugador.
  useEffect(() => {
    if (!esAdmin) return undefined;
    let vigente = true;
    const t = setTimeout(() => {
      getVistaPreviaAcceso(form.usuario.trim().toLowerCase() || '…', form.jugadorId)
        .then((v) => vigente && setVistaPrevia(v))
        .catch(() => vigente && setVistaPrevia(null));
    }, 300);
    return () => { vigente = false; clearTimeout(t); };
  }, [esAdmin, form.usuario, form.jugadorId]);

  if (!esAdmin) {
    return <div className="page"><p>Solo el administrador puede gestionar usuarios.</p></div>;
  }

  const ejecutar = async (accion) => {
    setError(null);
    setAviso(null);
    setOcupado(true);
    try {
      await accion();
    } catch (err) {
      setError(err.message || 'No se pudo completar la operación.');
    } finally {
      setOcupado(false);
    }
  };

  const crear = (e) => {
    e.preventDefault();
    return ejecutar(async () => {
      const r = await crearUsuario({
        usuario: form.usuario,
        email: form.email,
        rol: form.rol,
        jugadorId: form.jugadorId === '' ? null : Number(form.jugadorId),
        enviarAcceso: form.enviarAcceso,
      });
      setAlta(r);
      setForm(FORM_VACIO);
      await cargar();
    });
  };

  const guardar = (u, cambio) => ejecutar(async () => {
    const nuevo = await actualizarUsuario(u.id, cambio);
    setUsuarios((lista) => lista.map((x) => (x.id === nuevo.id ? nuevo : x)));
    setAviso(`Usuario ${nuevo.usuario} actualizado.`);
  });

  const cambiarActivo = (u, activo) => ejecutar(async () => {
    const nuevo = await actualizarUsuario(u.id, { activo });
    setUsuarios((lista) => lista.map((x) => (x.id === nuevo.id ? nuevo : x)));
  });

  const confirmarEnvio = () => {
    const u = confirmar;
    setConfirmar(null);
    return ejecutar(async () => {
      const r = await enviarAcceso(u.id);
      setAviso(`Acceso enviado a ${r.enviadoA}. La contraseña anterior ya no sirve.`);
      await cargar();
    });
  };

  const elegirJugador = (jugadorId) => {
    const j = jugadores.find((x) => String(x.id) === String(jugadorId));
    setForm((f) => ({
      ...f,
      jugadorId,
      // Propuestas editables: solo se pisan al elegir un jugador.
      usuario: j ? proponerUsuario(j.nombre, usuarios.map((u) => u.usuario)) : f.usuario,
      email: j?.email || f.email,
    }));
  };

  const jugadorObligatorio = form.rol === 'JUGADOR';

  return (
    <div className="page alta-usuario">
      <div className="page-header"><h2>Alta de usuarios</h2></div>
      <p>
        Crea cuentas de acceso y envíalas por correo. El rol y el jugador vinculado son independientes: un
        miembro del comité o un administrador puede ser también jugador (tendrá «Mi zona»).
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      {aviso && <p className="aviso-ok" role="status">{aviso}</p>}

      <form className="nueva-form" onSubmit={crear}>
        <label>Rol
          <select value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label>{jugadorObligatorio ? 'Jugador (sin usuario)' : 'También es jugador (sin usuario)'}
          <SelectJugador jugadores={jugadores} value={form.jugadorId} obligatorio={jugadorObligatorio}
            etiqueta="Jugador vinculado" onChange={elegirJugador} />
        </label>
        <label>Usuario
          <input required minLength={3} value={form.usuario} autoComplete="off"
            onChange={(e) => setForm({ ...form, usuario: e.target.value })} />
        </label>
        <label>Correo
          <input required type="email" value={form.email} autoComplete="off"
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="alta-check">
          <input type="checkbox" checked={form.enviarAcceso}
            onChange={(e) => setForm({ ...form, enviarAcceso: e.target.checked })} />
          Enviar el acceso por correo al crear
        </label>
        <button type="submit" className="btn btn-primary"
          disabled={ocupado || (jugadorObligatorio && form.jugadorId === '')}>
          Crear usuario
        </button>
      </form>

      <section className="alta-correo" aria-label="Correo que se enviará">
        <h3>Correo que se enviará al crear</h3>
        <p className="alta-correo-para">
          Para: <strong>{form.email || '(indica el correo)'}</strong>
          {!form.enviarAcceso && ' — desactivado: no se enviará al crear (usa «Enviar acceso» después)'}
        </p>
        {vistaPrevia && (
          <>
            <p><strong>Asunto:</strong> {vistaPrevia.asunto}</p>
            <pre>{vistaPrevia.texto}</pre>
          </>
        )}
      </section>

      {alta && (
        <div className="alta-resultado" role="status">
          <h3>Usuario «{alta.usuario.usuario}» creado</h3>
          {alta.correo.enviado
            ? <p>Se ha enviado el acceso a <strong>{alta.usuario.email}</strong>.</p>
            : (
              <>
                {alta.correo.error && <p className="error">No se pudo enviar el correo: {alta.correo.error}</p>}
                <p>Contraseña temporal (solo se muestra ahora): <code>{alta.contrasenaTemporal}</code></p>
                <p>Entrégasela por un canal seguro, o usa «Enviar acceso» en la lista (generará una nueva).</p>
              </>
            )}
          <button type="button" className="btn btn-secondary" onClick={() => setAlta(null)}>Cerrar</button>
        </div>
      )}

      <div className="table-wrap" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr><th>Usuario</th><th>Correo</th><th>Rol</th><th>Jugador</th><th>Estado</th><th>Acciones</th></tr>
          </thead>
          <tbody>
            {usuarios.map((u) => (
              <FilaUsuario key={`${u.id}-${u.rol}-${u.jugadorId}`} u={u} jugadores={jugadores} esYo={u.id === yo.id} ocupado={ocupado}
                onGuardar={guardar} onEnviar={setConfirmar} onActivo={cambiarActivo} />
            ))}
          </tbody>
        </table>
      </div>

      {confirmar && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmar(null)}>
          <div className="modal-box" role="dialog" aria-modal="true" aria-labelledby="enviar-acceso-titulo">
            <h3 id="enviar-acceso-titulo">Enviar acceso</h3>
            <p>
              Se generará una contraseña temporal nueva para <strong>{confirmar.usuario}</strong> y se enviará a{' '}
              <strong>{confirmar.email}</strong> con el enlace de acceso. Su contraseña actual dejará de valer y
              se cerrarán sus sesiones abiertas.
            </p>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmar(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={confirmarEnvio}>Enviar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
